import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  ImprovementSuggestion,
  ImprovementSuggestionAdoptionPayload,
  ImprovementSuggestionAttachment,
  ImprovementSuggestionPayload,
} from '../types/improvementSuggestion';

export async function fetchImprovementSuggestions(
  keyword?: string,
  contractId?: number | null
): Promise<ImprovementSuggestion[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<ImprovementSuggestion[]>('/improvement-suggestions', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchImprovementSuggestion(
  improvementSuggestionId: number
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.get<ImprovementSuggestion>(
    `/improvement-suggestions/${improvementSuggestionId}`
  );
  return data;
}

export async function createImprovementSuggestion(
  payload: ImprovementSuggestionPayload
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.post<ImprovementSuggestion>('/improvement-suggestions', payload);
  return data;
}

export async function updateImprovementSuggestion(
  improvementSuggestionId: number,
  payload: ImprovementSuggestionPayload
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.patch<ImprovementSuggestion>(
    `/improvement-suggestions/${improvementSuggestionId}`,
    payload
  );
  return data;
}

export async function deleteImprovementSuggestion(
  improvementSuggestionId: number
): Promise<void> {
  await apiClient.delete(`/improvement-suggestions/${improvementSuggestionId}`);
}

export async function approveImprovementSuggestion(
  improvementSuggestionId: number
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.post<ImprovementSuggestion>(
    `/improvement-suggestions/${improvementSuggestionId}/approve`
  );
  return data;
}

export async function revokeImprovementSuggestion(
  improvementSuggestionId: number
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.post<ImprovementSuggestion>(
    `/improvement-suggestions/${improvementSuggestionId}/revoke`
  );
  return data;
}

export async function updateImprovementSuggestionAdoption(
  improvementSuggestionId: number,
  payload: ImprovementSuggestionAdoptionPayload
): Promise<ImprovementSuggestion> {
  const { data } = await apiClient.patch<ImprovementSuggestion>(
    `/improvement-suggestions/${improvementSuggestionId}/adoption`,
    payload
  );
  return data;
}

export async function uploadImprovementSuggestionAttachments(
  improvementSuggestionId: number,
  files: File[]
): Promise<ImprovementSuggestionAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<ImprovementSuggestionAttachment[]>(
    `/improvement-suggestions/${improvementSuggestionId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteImprovementSuggestionAttachment(
  improvementSuggestionId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(
    `/improvement-suggestions/${improvementSuggestionId}/attachments/${attachmentId}`
  );
}

export async function downloadImprovementSuggestionAttachment(
  improvementSuggestionId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/improvement-suggestions/${improvementSuggestionId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadImprovementSuggestionPdf(
  improvementSuggestionId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/improvement-suggestions/${improvementSuggestionId}/pdf`,
    fileName || `개선제안서_${improvementSuggestionId}.pdf`
  );
}
